import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

// MUI Imports
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";

import { Typography, IconButton, Box } from "@mui/material";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";

// Local Imports
import StatusGenerator from "../StatusGenerator/StatusGenerator";
import {
  createPartner,
  editPartner,
  resetActionDialog,
  resetFetchPartnerById,
} from "../../Actions";
import CustomizedTabs from "./CustomizedTabs";

// import CloseIcon from "@mui/icons-material/Close";
// import Iconify from "../Dashboard/Iconify";
// import { PartnerExcelFile } from "../ExcelFiles/partner";
// import FileSaver from "file-saver";
// import {
//   DropActiveText,
//   DropZoneFooter,
//   DropZoneText,
//   TemplateNameSection,
//   TemplateSection,
//   TemplateZone,
//   dragdropActive,
//   dragdropZone,
//   errorColor,
//   fileNameSection,
//   filesUploadedZone,
// } from "../../Styles/DragnDrop";
// import { BulkUploadBeforeIcon } from "../Icons/FileUploadIcons";
// import BulkUploadAfterIcon from "../Icons/BulkUploadAfterIcon.gif";
// import { useDropzone } from "react-dropzone";

const StyledTextField = styled(TextField)(
  ({ theme, disabled, showBorder }) => ({
    "& .MuiOutlinedInput-root": {
      borderColor: showBorder ? undefined : "transparent !important",
      borderWidth: showBorder ? undefined : 0,
      borderRadius: showBorder ? undefined : theme.shape.borderRadius,
      "&.Mui-focused": {
        borderColor: showBorder ? undefined : "transparent !important",
        borderWidth: showBorder ? undefined : 0,
        borderRadius: showBorder ? undefined : theme.shape.borderRadius,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      color: "black",
      "-webkit-text-fill-color": "black",
    },
  })
);

function MyTextField({ showBorder = true, ...props }) {
  return <StyledTextField showBorder={showBorder} {...props} />;
}

const GeneralDialog = ({
  isOpen, //bool
  isFetchingDialogData, // bool
  onCloseHandler, //fn:null
  states, // state => array of objects
  mode, // Create || Edit || View
  modeSetter,
  fireFetchAllPartnersAPI,
}) => {
  const dispatch = useDispatch();
  const partnerState = (state) => state.partnersReducer;
  const {
    singlePartnerDetail,
    makingRequest,
    dialogSaving,
    dialogSaveSuccess,
  } = useSelector(partnerState);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [partnerCityValue, setPartnerCityValue] = useState({
    label: "", // name of the state
    value: null, // id of the state
  });

  const [partnerDetails, setPartnerDetails] = useState({
    contactAddress: "",
    email: "",
    name: "",
    phoneNumber: "",
    physicalAddress: "",
    purposeOfPartnership: "",
    stateId: "",
    webSite: "",
  });

  const [dialogErrors, setDialogErrors] = useState({
    partnerName: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    emailAddress: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    phoneNumber: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    physicalAddress: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    state: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    purposeOfPartnership: {
      hasError: true,
      errorText: "This is a required field",
      showError: false,
    },
    partnerLogo: {
      hasError: true,
      errorText: "Logo is required",
      showError: false,
    },
  });

  const [partnerLogo, setPartnerLogo] = useState({
    name: "",
    file: "",
    contentType: "",
  });

  // validation functions
  /**
   * Returns `true` if input string contains only alphabet?
   */
  function isAlphabetic(str) {
    return /^[a-zA-Z\s]+$/.test(str);
  }

  /**
   * returns `true` if email is valid
   *
   * Format: `a@a.a`
   */
  function validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  /**
   * Returns `true` if input string contains only numbers and + character
   */
  function containsOnlyNumbers(inputString) {
    const regex = /^[0-9+]+$/;
    return regex.test(inputString);
  }
  /**
   * Checks if any error is present.
   * Returns `true` once an error is discovered
   */
  function checkForError(obj) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key].hasError) {
          return true;
        }
      }
    }
    return false;
  }

  function modifyAllErrors(obj, condition = true) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        obj[key].showError = condition;
      }
    }
    return obj;
  }

  useEffect(() => {
    // populate local store with data from API
    if (mode !== "Create" && Object.keys(singlePartnerDetail)?.length > 0) {
      setPartnerDetails(singlePartnerDetail);

      if (singlePartnerDetail.file !== null) {
        setPartnerLogo({
          name: singlePartnerDetail.file.name,
          file: singlePartnerDetail.file.file,
          contentType: singlePartnerDetail.file.contentType,
        });
      }

      if (singlePartnerDetail.state !== null) {
        setPartnerCityValue({
          label: singlePartnerDetail.state.state,
          value: singlePartnerDetail.stateID,
        });
      }

      if (mode === "Edit") {
        if (Object.keys(singlePartnerDetail)?.length > 0) {
          let testObj = {
            ...dialogErrors,
          };
          for (const key in singlePartnerDetail) {
            if (
              [
                "name",
                "email",
                "physicalAddress",
                "purposeOfPartnership",
              ].includes(key)
            ) {
              if (singlePartnerDetail[key].length > 0) {
                if (key === "email") {
                  // email check and conversion
                  if (!validateEmail(singlePartnerDetail[key])) {
                    testObj["emailAddress"].hasError = true;
                    testObj["emailAddress"].showError = true;
                    testObj["emailAddress"].errorText =
                      "Enter a valid email address";
                  }
                } else if (key === "name") {
                  // name check and conversion
                  testObj["partnerName"].hasError = false;
                  testObj["partnerName"].showError = false;
                  testObj["partnerName"].errorText = "";
                } else {
                  testObj[key].hasError = false;
                  testObj[key].showError = false;
                  testObj[key].errorText = "";
                }
              }
            } else if (key === "phoneNumber") {
              if (!containsOnlyNumbers(singlePartnerDetail[key])) {
                testObj[key].hasError = true;
                testObj[key].showError = true;
                testObj[key].errorText =
                  "Phone Number cannot contain non-numeric characters";
              } else if (singlePartnerDetail[key] > 0) {
                testObj[key].hasError = false;
                testObj[key].showError = false;
                testObj[key].errorText = "";
              }
            } else if (key === "state") {
              if (singlePartnerDetail[key]?.id >= 0) {
                testObj[key].hasError = false;
                testObj[key].showError = false;
                testObj[key].errorText = "";
              }
            }
          }

          setDialogErrors({
            ...dialogErrors,
            ...testObj,
          });
        }
      }
    }
  }, [mode, singlePartnerDetail]);

  useEffect(() => {
    if (dialogSaveSuccess) {
      fireFetchAllPartnersAPI();
      closeGeneralDialog();
      dispatch(resetActionDialog());
    }
  }, [dialogSaveSuccess]);

  const modes = {
    Create: {
      title: "Add New Partner",
    },
    View: {
      title: "View Partner",
    },
    Edit: {
      title: "Edit Partner",
    },
  };

  const formFields = [
    {
      // partner name
      label: "Partner Name",
      error:
        dialogErrors.partnerName.showError && dialogErrors.partnerName.hasError,
      helperText:
        dialogErrors.partnerName.showError &&
        dialogErrors.partnerName.errorText,
      value: partnerDetails.name,
      onChange: (newValue) => {
        if (newValue === "") {
          setPartnerDetails({ ...partnerDetails, name: newValue });
          setDialogErrors({
            ...dialogErrors,
            partnerName: {
              hasError: true,
              errorText: "Partner Name can't be empty",
              showError: true,
            },
          });
        } else if (isAlphabetic(newValue) && newValue !== " ") {
          setPartnerDetails({ ...partnerDetails, name: newValue });
          setDialogErrors({
            ...dialogErrors,
            partnerName: {
              hasError: false,
              errorText: "",
              showError: false,
            },
          });
        }
      },
      placeholder: mode === "View" ? "" : "Type name here",
      multiline: false,
      rows: 0,
      multiple: false,
      autoFocus: true,
      required: true,
    },
    {
      // email address
      label: "Email Address",
      error:
        dialogErrors.emailAddress.showError &&
        dialogErrors.emailAddress.hasError,
      helperText:
        dialogErrors.emailAddress.showError &&
        dialogErrors.emailAddress.errorText,
      value: partnerDetails.email,
      onChange: (newValue) => {
        if (validateEmail(newValue)) {
          // if it is a valid email address
          setPartnerDetails({ ...partnerDetails, email: newValue });

          setDialogErrors({
            ...dialogErrors,
            emailAddress: {
              hasError: false,
              errorText: "",
              showError: false,
            },
          });
        } else {
          // if it is not yet a valid email address
          setPartnerDetails({ ...partnerDetails, email: newValue });
          setDialogErrors({
            ...dialogErrors,
            emailAddress: {
              hasError: true,
              errorText: "Please enter a valid e-mail Address",
              showError: true,
            },
          });
        }
      },
      placeholder: "",
      multiline: false,
      rows: 0,
      multiple: false,
      autoFocus: false,
      required: true,
    },
    {
      // phone number
      label: "Phone Number",
      error:
        dialogErrors.phoneNumber.showError && dialogErrors.phoneNumber.hasError,
      helperText:
        dialogErrors.phoneNumber.showError &&
        dialogErrors.phoneNumber.errorText,
      value: partnerDetails.phoneNumber,
      onChange: (newValue) => {
        if (newValue === "") {
          setPartnerDetails({ ...partnerDetails, phoneNumber: newValue });
          setDialogErrors({
            ...dialogErrors,
            phoneNumber: {
              hasError: true,
              errorText: "This is a required field",
              showError: true,
            },
          });
        } else if (containsOnlyNumbers(newValue)) {
          // if valid phone number

          setPartnerDetails({ ...partnerDetails, phoneNumber: newValue });
          setDialogErrors({
            ...dialogErrors,
            phoneNumber: {
              hasError: false,
              errorText: "",
              showError: false,
            },
          });

          if (newValue.length < 10 || newValue.length > 11) {
            setDialogErrors({
              ...dialogErrors,
              phoneNumber: {
                hasError: true,
                errorText: "Input a valid phone number",
                showError: true,
              },
            });
          }
        }
      },
      placeholder: "",
      multiline: false,
      rows: 0,
      multiple: false,
      autoFocus: false,
      required: true,
    },
    {
      //physical address
      label: "Physical Address",
      error:
        dialogErrors.physicalAddress.showError &&
        dialogErrors.physicalAddress.hasError,
      helperText:
        dialogErrors.physicalAddress.showError &&
        dialogErrors.physicalAddress.errorText,
      value: partnerDetails.physicalAddress,
      onChange: (newValue) => {
        if (newValue === "") {
          setPartnerDetails({
            ...partnerDetails,
            physicalAddress: newValue,
            contactAddress: newValue,
          });
          setDialogErrors({
            ...dialogErrors,
            physicalAddress: {
              hasError: true,
              errorText: "Physical Address is a required field",
              showError: true,
            },
          });
        } else {
          setPartnerDetails({
            ...partnerDetails,
            physicalAddress: newValue,
            contactAddress: newValue,
          });
          setDialogErrors({
            ...dialogErrors,
            physicalAddress: {
              hasError: false,
              errorText: "",
              showError: false,
            },
          });
        }
      },
      placeholder: "",
      multiline: false,
      rows: 0,
      multiple: false,
      autoFocus: false,
      required: true,
    },
    {
      multiple: true,
      fields: [
        {
          // website
          label: "Website",
          error: false,
          helperText: "",
          value: partnerDetails.webSite,
          onChange: (newValue) =>
            setPartnerDetails({ ...partnerDetails, webSite: newValue }),
          placeholder: "",
          multiline: false,
          rows: 0,
          multiple: false,
          autoFocus: false,
          required: false,
        },
        {
          // state -> autocomplete
          autoComplete: true,
          label: "State",
          error: dialogErrors.state.showError && dialogErrors.state.hasError,
          helperText:
            dialogErrors.state.showError && dialogErrors.state.errorText,
          value: partnerCityValue,
          onChange: (newValue) => {
            if (newValue !== null) {
              {
                setDialogErrors({
                  ...dialogErrors,
                  state: {
                    hasError: false,
                    errorText: "",
                    showError: false,
                  },
                });
                setPartnerCityValue({
                  label: newValue.label,
                  value: newValue.value,
                });
              }
            }
          },
          options: states,
          placeholder: "",
          multiline: false,
          rows: 0,
          multiple: false,
          autoFocus: false,
          required: true,
        },
      ],
    },
    {
      // purpose of partnership
      label: "Purpose of Partnership",
      error:
        dialogErrors.purposeOfPartnership.showError &&
        dialogErrors.purposeOfPartnership.hasError,
      helperText:
        dialogErrors.purposeOfPartnership.showError &&
        dialogErrors.purposeOfPartnership.errorText,
      value: partnerDetails.purposeOfPartnership,
      onChange: (newValue) => {
        if (newValue === "") {
          setPartnerDetails({
            ...partnerDetails,
            purposeOfPartnership: newValue,
          });
          setDialogErrors({
            ...dialogErrors,
            purposeOfPartnership: {
              hasError: true,
              errorText: "This is a required field",
              showError: true,
            },
          });
        } else {
          setPartnerDetails({
            ...partnerDetails,
            purposeOfPartnership: newValue,
          });
          setDialogErrors({
            ...dialogErrors,
            purposeOfPartnership: {
              hasError: false,
              errorText: "",
              showError: false,
            },
          });
        }
      },
      placeholder: "",
      multiline: true,
      rows: 3,
      multiple: false,
      autoFocus: false,
      required: true,
    },
  ];

  const closeGeneralDialog = () => {
    onCloseHandler();
    dispatch(resetFetchPartnerById());

    // state resets
    const freshDialogErrorState = modifyAllErrors(dialogErrors, false);
    setDialogErrors({ ...freshDialogErrorState });

    setPartnerDetails({
      contactAddress: "",
      email: "",
      name: "",
      phoneNumber: "",
      physicalAddress: "",
      purposeOfPartnership: "",
      stateId: "",
      webSite: "",
    });

    setPartnerCityValue(null);

    setPartnerLogo({
      name: "",
      file: "",
      contentType: "",
    });
  };

  const addBulkUploadPartner = () => {
    // debugger;
    // if (bulkFile?.name !== "") {
    //   const formData = new FormData();
    //   formData.append("File", bulkFileUpload);
    //   dispatch(addBulkPartner(formData));
    //   setLoadingButton(true);
    //   setDisable(colors.secondaryGreen);
    // } else {
    //   setBulkFile({ ...bulkFile, emptyError: true });
    // }
    // setTimeout(() => {
    //   if (dispatch) {
    //     setLoadingButton(false);
    //   }
    // }, 2000);
  };

  const createAPartner = () => {
    const requestModel = {
      ...partnerDetails,
      stateId: partnerCityValue.value,
      file: {
        ...partnerLogo,
        name: partnerLogo.name[0],
      },
    };

    dispatch(createPartner(requestModel));
    // dispatch(makeRequestPartners());
  };

  const onPrimButtonClickHandler = (mode) => {
    if (checkForError(dialogErrors)) {
      // if there are still errors
      const newErrorState = modifyAllErrors(dialogErrors);
      setDialogErrors({ ...newErrorState });
    } else {
      // if we are clear for submission
      if (mode === "Create") {
        if (value === 0) {
          createAPartner();
        } else {
          addBulkUploadPartner();
        }
      } else if (mode === "Edit") {
        let editModel = {};

        for (let key in partnerDetails) {
          if (key !== "state" && key !== "id") {
            editModel[key] = partnerDetails[key];
          }
        }

        editModel["stateId"] = partnerCityValue.value;
        editModel["partnerId"] = partnerDetails.id;

        dispatch(editPartner(editModel));
        // dispatch(makeRequestPartners());
      }
    }
  };

  // const uploadedItemRef = useRef(null);

  // const [bulkFileUpload, setBulkFileUpload] = useState(null);
  // const initialBulkFileValues = {
  //   name: "",
  //   path: "",
  //   size: "",
  //   display: false,
  //   error: false,
  //   emptyError: false,
  // };
  // const [bulkFile, setBulkFile] = useState(initialBulkFileValues);
  // const handleDeleteFile = () => {
  //   setBulkFile(initialBulkFileValues);
  //   setBulkFileUpload(null);
  // };

  // function MyDropzone() {
  //   const onDrop = useCallback((acceptedFiles) => {
  //     uploadedItemRef?.current?.scrollIntoView({ behavior: "smooth" });
  //     acceptedFiles.forEach((file) => {
  //       const reader = new FileReader();
  //       reader.onabort = () => console.log("file reading was aborted");
  //       reader.onerror = () => console.log("file reading has failed");
  //       reader.onload = () => {
  //         // Do whatever you want with the file contents
  //         const binaryStr = reader.result;
  //         console.log(binaryStr, "binarystring");
  //         console.log(reader, "reader");
  //         console.log(file, "file");
  //         if (!file.name.match(/\.(xlsx)$/)) {
  //           setBulkFile({
  //             ...bulkFile,
  //             error: true,
  //             display: false,
  //             name: "",
  //             path: "",
  //             size: "",
  //           });
  //           setBulkFileUpload(null);
  //           return false;
  //         } else {
  //           setBulkFileUpload(file);
  //           setBulkFile({
  //             ...bulkFile,
  //             name: file.name,
  //             size: file.size,
  //             display: true,
  //             error: false,
  //             emptyError: false,
  //           });
  //         }
  //       };
  //       reader.readAsArrayBuffer(file);
  //       console.log(reader.readAsArrayBuffer(file), "as array buffer");
  //     });
  //   }, []);
  //   const { getRootProps, getInputProps, isDragActive } = useDropzone({
  //     onDrop,
  //   });

  //   return (
  //     <>
  //       <div
  //         style={isDragActive ? dragdropActive : dragdropZone}
  //         {...getRootProps()}
  //       >
  //         <div>
  //           {/* <img src={Folder} alt="folder" width="70px" height="50px" /> */}
  //         </div>
  //         <br />
  //         <input {...getInputProps()} />
  //         {isDragActive ? (
  //           <>
  //             <div style={{ textAlign: "center" }}>
  //               <img
  //                 src={BulkUploadAfterIcon}
  //                 alt="file upload"
  //                 width={50}
  //                 height={50}
  //               />
  //               <p style={DropActiveText}>
  //                 {" "}
  //                 <b>Drop file here...</b>
  //               </p>
  //             </div>
  //           </>
  //         ) : (
  //           <>
  //             <div style={{ textAlign: "center" }}>
  //               <BulkUploadBeforeIcon />
  //               <p style={DropZoneText}>Upload the filled-in template here</p>
  //               <p style={{ fontSize: "10px" }}>
  //                 Drag and drop, or click to select
  //               </p>
  //             </div>
  //           </>
  //         )}
  //       </div>
  //       <div style={DropZoneFooter}>
  //         <div>
  //           <p>File must be in .xlsx</p>
  //         </div>
  //         <div>
  //           <p>Maximum Size: 20MB</p>
  //         </div>
  //       </div>
  //     </>
  //   );
  // }

  // const handleDownload = (e) => {
  //   e.preventDefault();
  //   // let dataBlob = PartnerExcelFile;
  //   let sliceSize = 1024;
  //   let byteCharacters = atob(PartnerExcelFile);
  //   let bytelength = byteCharacters.length;
  //   let sliceCount = Math.ceil(bytelength / sliceSize);
  //   let byteArrays = new Array(sliceCount);
  //   for (let sliceIndex = 0; sliceIndex < sliceCount; ++sliceIndex) {
  //     let begin = sliceIndex * sliceSize;
  //     let end = Math.min(begin + sliceSize, bytelength);
  //     let bytes = new Array(end - begin);
  //     for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
  //       bytes[i] = byteCharacters[offset].charCodeAt(0);
  //     }
  //     byteArrays[sliceIndex] = new Uint8Array(bytes);
  //   }
  //   let blob = new Blob(byteArrays, { type: "application/vnd.ms-excel" });
  //   FileSaver.saveAs(new Blob([blob], {}), "Partner-Template.xlsx");
  // };

  // for the dialog tabs control

  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // function handleLogoUpload() {

  // }

  function handleLogoDelete() {
    setPartnerLogo({
      name: "",
      file: "",
      contentType: "",
    });
    setDialogErrors({
      ...dialogErrors,
      partnerLogo: {
        hasError: true,
        errorText: "Partner Logo is required",
        showError: false,
      },
    });
  }

  function handleFileSelect(event) {
    const { files } = event.target;
    const fileName = files[0].name.split(".");
    const fileExtension = fileName[1];
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      const base64 = e.target.result;

      setPartnerLogo({
        name: partnerDetails.name + " logo",
        file: base64,
        contentType: fileExtension,
      });

      setDialogErrors({
        ...dialogErrors,
        partnerLogo: {
          hasError: false,
          errorText: "",
          showError: false,
        },
      });
    };

    fileReader.readAsDataURL(files[0]);
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={closeGeneralDialog}
      PaperProps={{
        style: {
          borderRadius: 20,
          padding: 8,
          width: "37rem",
          height: "fit-content",
        },
      }}
    >
      <DialogTitle>
        <Grid container sx={{ justifyContent: "space-between" }}>
          {modes[mode]?.title} {/* title of the form */}
          {mode !== "Create" &&
            Object.keys(partnerDetails).findIndex(
              (key) => key === "isActive"
            ) !== -1 && ( // display status of partner - active or inactive
              <StatusGenerator
                statusText={partnerDetails?.isActive ? "Active" : "Inactive"}
                variant={partnerDetails?.isActive ? "greenFilled" : "redFilled"}
              />
            )}
        </Grid>
      </DialogTitle>
      <Divider />

      {/* main dialog content */}
      <DialogContent>
        <CustomizedTabs
          value={value}
          handleChange={handleChange}
          Item1={
            <Box
              component="form"
              sx={{
                "& > :not(style)": { p: 2 },
              }}
              noValidate
              autoComplete="off"
            >
              <Grid container spacing={3} direction="column">
                <Typography
                  sx={{
                    padding: "12px 0 0 24px",
                    fontSize: "13px",
                    color: "rgba(0,0,0,0.47)",
                  }}
                >
                  Fields marked with * are required
                </Typography>

                {formFields.map((field, index) => {
                  if (field.multiple) {
                    // renders two textfields in one row
                    return (
                      <Grid
                        container
                        item
                        xs
                        spacing={3}
                        sx={{ justifyContent: "space-between" }}
                        key={index}
                      >
                        {field.fields.map((field, index) => {
                          return (
                            <Grid item xs={6} key={index}>
                              {isFetchingDialogData ? (
                                <Skeleton
                                  variant="rounded"
                                  animation="wave"
                                  height={40}
                                  width={223}
                                />
                              ) : !field?.autoComplete ? (
                                <MyTextField
                                  error={field.error}
                                  helperText={field.helperText}
                                  value={field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  label={field.label}
                                  variant="outlined"
                                  color="primary"
                                  required={field.required}
                                  fullWidth
                                  size="small"
                                  disabled={
                                    isFetchingDialogData ||
                                    makingRequest ||
                                    mode === "View"
                                  }
                                  showBorder={mode !== "View"}
                                ></MyTextField>
                              ) : (
                                <Autocomplete
                                  value={field.value}
                                  onChange={(event, newValue) => {
                                    field.onChange(newValue);
                                  }}
                                  options={field.options}
                                  size="small"
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      error={field.error}
                                      helperText={field.helperText}
                                      label="State*"
                                    />
                                  )}
                                  fullWidth
                                  readOnly={
                                    isFetchingDialogData ||
                                    makingRequest ||
                                    mode === "View"
                                  }
                                />
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  } else {
                    // renders one textfield per row
                    return (
                      <Grid item xs key={field.label}>
                        {isFetchingDialogData ? (
                          <Skeleton
                            variant="rounded"
                            animation="wave"
                            height={40}
                            width={470}
                          />
                        ) : (
                          <MyTextField
                            error={field.error}
                            helperText={field.helperText}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            label={field.label}
                            placeholder={field.placeholder}
                            multiline={field.multiline}
                            rows={field.multiline ? field.rows : null}
                            autoFocus={field.autoFocus}
                            variant="outlined"
                            color="primary"
                            required={field.required}
                            fullWidth
                            size="small"
                            disabled={
                              isFetchingDialogData ||
                              makingRequest ||
                              mode === "View"
                            }
                            showBorder={mode !== "View"}
                          ></MyTextField>
                        )}
                      </Grid>
                    );
                  }
                })}

                <>
                  {isFetchingDialogData ? (
                    <Grid item xs>
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={40}
                        width={470}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs>
                      {partnerLogo.file.length < 1 ? (
                        <>
                          {mode !== "View" && (
                            <Button
                              variant={"outlined"}
                              startIcon={
                                <UploadFileRoundedIcon
                                  sx={
                                    dialogErrors.partnerLogo.showError &&
                                    dialogErrors.partnerLogo.errorText
                                      ? "#d32f2f"
                                      : "#137542"
                                  }
                                />
                              }
                              component="label"
                              color={
                                dialogErrors.partnerLogo.showError &&
                                dialogErrors.partnerLogo.errorText
                                  ? "error"
                                  : "success"
                              }
                            >
                              Upload Logo*
                              <input
                                hidden
                                type="file"
                                id="uploadedLogo"
                                onChange={(event) => handleFileSelect(event)}
                              />
                            </Button>
                          )}
                        </>
                      ) : (
                        <div
                          style={{
                            maxWidth: "200px",
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid rgba(0, 0, 0, 0.87)",
                              borderTopLeftRadius: "4px",
                              borderTopRightRadius: "4px",
                              borderBottom: "none",
                              padding: "0px 8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              minHeight: "2.5rem",
                            }}
                          >
                            Partner Logo
                            {mode !== "View" && (
                              <IconButton onClick={handleLogoDelete}>
                                <DeleteOutlineRoundedIcon />
                              </IconButton>
                            )}
                          </div>
                          <div>
                            <img
                              style={{
                                maxWidth: "200px",
                                height: "auto",
                              }}
                              src={partnerLogo?.file}
                              alt={partnerLogo?.name}
                            />
                          </div>
                        </div>
                      )}
                      <Typography
                        variant="body2"
                        style={{
                          color: "#d32f2f",
                          fontSize: "12px",
                          margin: "4px 0px 0px 14px",
                        }}
                      >
                        {dialogErrors.partnerLogo.showError &&
                          dialogErrors.partnerLogo.errorText}
                      </Typography>
                    </Grid>
                  )}
                </>
              </Grid>
            </Box>
          }
          // Item2={
          //   <Box
          //     component="form"
          //     sx={{
          //       "& > :not(style)": { pt: 2, pb: 0, pl: 2 },
          //       fontFamily: Typography.secondaryFont,
          //       textTransform: "initial",
          //     }}
          //     noValidate
          //     autoComplete="off"
          //   >
          //     <Box>
          //       <div style={{ marginTop: "2px" }}>
          //         {bulkFile.error ? (
          //           <p style={errorColor}>
          //             You can only upload file in .XLSX format{" "}
          //           </p>
          //         ) : bulkFile.emptyError ? (
          //           <p style={errorColor}>Please upload a file to submit</p>
          //         ) : (
          //           ""
          //         )}
          //         <MyDropzone />
          //       </div>
          //       {bulkFile.display ? (
          //         <div style={filesUploadedZone} ref={uploadedItemRef}>
          //           <div style={fileNameSection}>
          //             <div
          //               style={{
          //                 backgroundColor: "#fff",
          //                 padding: "7px",
          //                 border: "1.3px solid #e0e0e0",
          //                 borderRadius: "7pX",
          //                 width: "fit-content",
          //               }}
          //             >
          //               <Iconify
          //                 icon="vscode-icons:file-type-excel"
          //                 style={{ width: "30px", height: "30px" }}
          //               />
          //             </div>
          //             <div>
          //               <p style={{ fontWeight: 600 }}>{bulkFile.name}</p>
          //               <p style={{ marginTop: "10px", fontWeight: 400 }}>
          //                 {Math.round(bulkFile.size / 1024)} KB
          //               </p>
          //             </div>
          //           </div>
          //           <>
          //             <IconButton
          //               size="small"
          //               sx={{ marginTop: "-15px" }}
          //               onClick={handleDeleteFile}
          //             >
          //               <CloseIcon sx={{ fontSize: "1.2rem" }} />
          //             </IconButton>
          //           </>
          //         </div>
          //       ) : (
          //         <div style={TemplateZone} ref={uploadedItemRef}>
          //           <div>
          //             <div style={TemplateNameSection}>
          //               <div>
          //                 {" "}
          //                 <Iconify
          //                   icon="vscode-icons:file-type-excel"
          //                   style={{ width: "30px", height: "30px" }}
          //                 />
          //               </div>

          //               <div>
          //                 <p style={{ fontWeight: 600 }}>
          //                   Partners Template Sheet
          //                 </p>
          //               </div>
          //             </div>
          //             <div>
          //               <p
          //                 style={{
          //                   marginTop: "10px",
          //                   fontWeight: 400,
          //                   color: "9DA3AA",
          //                   width: "90%",
          //                 }}
          //               >
          //                 Download the sample template by clicking on the
          //                 download button. Add your data to the template file
          //                 and upload the file for submission.{" "}
          //               </p>
          //             </div>
          //           </div>
          //           <>
          //             <Button
          //               onClick={handleDownload}
          //               variant="outlined"
          //               disableElevation
          //               sx={{
          //                 fontSize: "13px",
          //                 padding: "10px 25px",
          //                 background: "#fff",
          //                 border: "1.3px solid #e0e0e0",
          //                 borderRadius: "5px",
          //                 textTransform: "initial",
          //                 color: "gray",
          //                 "&:hover": { background: "#fff", color: "#016c34" },
          //               }}
          //             >
          //               Download
          //             </Button>
          //           </>
          //         </div>
          //       )}
          //     </Box>
          //   </Box>
          // }
        />
      </DialogContent>
      <Divider />
      {
        <DialogActions>
          {mode === "View" && (
            <Button
              sx={{
                border: "2px solid #F1D74F",
                color: "#F1D74F",
                "&:hover": {
                  border: "2px solid #F1D74F",
                  color: "#F1D74F",
                },
              }}
              variant="outlined"
              onClick={() => modeSetter("Edit")}
              disabled={dialogSaving}
            >
              Edit
            </Button>
          )}
          <Button
            color="error"
            variant="outlined"
            onClick={closeGeneralDialog}
            disabled={dialogSaving}
          >
            {mode === "View" ? <>Close</> : <>Cancel</>}
          </Button>

          {mode !== "View" && (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={() => onPrimButtonClickHandler(mode)}
                disabled={dialogSaving}
                startIcon={dialogSaving && <CircularProgress size={20} />}
              >
                {dialogSaving === true ? <> Saving...</> : <> Save </>}
              </Button>
            </>
          )}
        </DialogActions>
      }
    </Dialog>
  );
};

export default GeneralDialog;
